// Coordinate / render-frame analysis for the global Layer Properties console.
//
// Pure functions that fuse the viewer Layer's CRS fields, live engine render
// frame, and a session/local override into the Coordinates-tab view model.
// Layer-KIND aware (3D Tiles bbox assumption differs), never layer-id aware.

import type { Layer } from '@geosoft/viewer-sdk';
import type { LayerAlignmentStatus } from '@/engines/contract/diagnostics';
import type {
  CoordinateScopes,
  LayerCoordinateDiagnostics,
  LayerCoordinateOverrideInput,
  LiveRenderDiagnostics,
} from './types';

export interface CoordinateGroupMember {
  readonly id: string;
  readonly sourceCrs?: string | null;
  readonly displayCrs?: string | null;
}

const GEOGRAPHIC_FRAME_CRS = new Set(['EPSG:4326', 'EPSG:3857']);

const ALIGNMENT_VALUES: ReadonlySet<LayerAlignmentStatus> = new Set<LayerAlignmentStatus>([
  'aligned',
  'rejected',
  'fallback-3857',
  'frame-switched',
  'not-applicable',
  'unknown',
]);

export function normalizeCrs(value: string | undefined | null): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed.toUpperCase() : null;
}

/** True for CRS that need no display-CRS reconciliation in the Web-Mercator frame. */
export function isWebFrameCrs(crs: string | null): boolean {
  return crs !== null && GEOGRAPHIC_FRAME_CRS.has(crs);
}

/** A projected/engineering CRS that is neither geographic nor Web-Mercator. */
export function isProjectedNonWebCrs(crs: string | null): boolean {
  return crs !== null && !GEOGRAPHIC_FRAME_CRS.has(crs);
}

function formatCoordinate(value: number): string {
  return Number.isFinite(value) ? value.toFixed(6) : 'n/a';
}

export function formatBbox(
  bbox: readonly number[] | undefined | null,
): string | null {
  if (!Array.isArray(bbox) || bbox.length < 4) return null;
  return bbox.slice(0, 4).map((v) => formatCoordinate(v as number)).join(', ');
}

export function bboxCenter(
  bbox: readonly number[] | undefined | null,
): string | null {
  if (!Array.isArray(bbox) || bbox.length < 4) return null;
  const [minX, minY, maxX, maxY] = bbox as number[];
  return `${formatCoordinate((minX + maxX) / 2)}, ${formatCoordinate((minY + maxY) / 2)}`;
}

function formatAnchor(layer: Layer): string | null {
  const anchor = layer.anchor;
  if (!anchor || typeof anchor.lat !== 'number' || typeof anchor.lon !== 'number') {
    return null;
  }
  const parts = [`lat ${anchor.lat.toFixed(6)}`, `lon ${anchor.lon.toFixed(6)}`];
  if (typeof anchor.height === 'number') parts.push(`h ${anchor.height.toFixed(2)}m`);
  if (typeof anchor.heading === 'number') parts.push(`hdg ${anchor.heading.toFixed(1)}°`);
  return parts.join(', ');
}

function coerceAlignmentStatus(
  value: string | undefined | null,
): LayerAlignmentStatus | null {
  if (typeof value !== 'string') return null;
  return ALIGNMENT_VALUES.has(value as LayerAlignmentStatus)
    ? (value as LayerAlignmentStatus)
    : null;
}

/** Effective display CRS after applying any session/local override. */
export function effectiveDisplayCrs(
  layer: Layer,
  override?: LayerCoordinateOverrideInput | null,
): { displayCrs: string | null; displayCrsProj4: string | null; overridden: boolean } {
  const overrideCrs = normalizeCrs(override?.displayCrs ?? null);
  const overrideProj4 =
    typeof override?.displayCrsProj4 === 'string' && override.displayCrsProj4.trim().length > 0
      ? override.displayCrsProj4
      : null;
  if (overrideCrs) {
    return { displayCrs: overrideCrs, displayCrsProj4: overrideProj4, overridden: true };
  }
  const baseProj4 =
    typeof layer.displayCrsProj4 === 'string' && layer.displayCrsProj4.trim().length > 0
      ? layer.displayCrsProj4
      : null;
  return {
    displayCrs: normalizeCrs(layer.displayCrs),
    displayCrsProj4: baseProj4,
    overridden: false,
  };
}

/**
 * Resolve the render frame CRS the layer is (or would be) drawn in. Prefers the
 * live engine value; otherwise mirrors the Giro3D adapter rule: render in
 * `displayCrs` only when a proj4 def is available, else fall back to EPSG:3857.
 */
export function resolveFrameCrs(
  layer: Layer,
  override?: LayerCoordinateOverrideInput | null,
  live?: LiveRenderDiagnostics | null,
): string | null {
  const liveFrame = normalizeCrs(live?.frameCrs ?? null);
  if (liveFrame) return liveFrame;
  const { displayCrs, displayCrsProj4 } = effectiveDisplayCrs(layer, override);
  if (displayCrs && displayCrs !== 'EPSG:3857' && displayCrsProj4) {
    return displayCrs;
  }
  return 'EPSG:3857';
}

function deriveAlignmentStatus(
  layer: Layer,
  override: LayerCoordinateOverrideInput | null | undefined,
  live: LiveRenderDiagnostics | null | undefined,
): LayerAlignmentStatus {
  const fromLive = coerceAlignmentStatus(live?.alignmentStatus);
  if (fromLive) return fromLive;

  const { displayCrs, displayCrsProj4 } = effectiveDisplayCrs(layer, override);
  const sourceCrs = normalizeCrs(layer.sourceCrs ?? layer.crs);

  if (displayCrs && displayCrs !== 'EPSG:3857') {
    return displayCrsProj4 ? 'frame-switched' : 'fallback-3857';
  }
  if (isProjectedNonWebCrs(sourceCrs)) {
    // Projected source with no display CRS: alignment outcome is uncertain
    // (the warnings layer flags this as a likely misalignment).
    return 'unknown';
  }
  return 'not-applicable';
}

/**
 * Most common non-null value in `values`, or `null` when there is none.
 * Ties resolve to the first-seen value (stable).
 */
function modeOf(values: ReadonlyArray<string | null>): string | null {
  const counts = new Map<string, number>();
  for (const v of values) {
    if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [v, c] of counts) {
    if (c > bestCount) {
      best = v;
      bestCount = c;
    }
  }
  return best;
}

/**
 * Build the viewer / group / layer coordinate readout. Pure: the caller supplies
 * the active viewer frame CRS and the layer's group members (siblings in the
 * same slot). Surfaces the "camera/CRS confusion" by flagging when the scopes
 * disagree on a single CRS.
 */
export function buildCoordinateScopes(input: {
  readonly layerId: string;
  readonly sourceCrs: string | null;
  readonly displayCrs: string | null;
  readonly frameCrs: string | null;
  readonly viewerFrameCrs?: string | null;
  readonly groupLabel?: string | null;
  readonly groupMembers?: ReadonlyArray<CoordinateGroupMember>;
}): CoordinateScopes {
  const { sourceCrs, displayCrs, frameCrs } = input;
  const viewerFrame = normalizeCrs(input.viewerFrameCrs ?? null) ?? frameCrs;

  // Layer scope: what this layer wants to be drawn as. Prefer its display CRS
  // (the target frame) and fall back to its source CRS.
  const layerCrs = displayCrs ?? sourceCrs;
  const layer = {
    level: 'layer' as const,
    label: 'Layer',
    crs: layerCrs,
    note:
      displayCrs && sourceCrs && displayCrs !== sourceCrs
        ? `source ${sourceCrs} → display ${displayCrs}`
        : sourceCrs && !displayCrs
          ? 'no display CRS (renders in viewer frame)'
          : null,
  };

  const viewer = {
    level: 'viewer' as const,
    label: 'Viewer render frame',
    crs: viewerFrame,
    note: viewerFrame === 'EPSG:3857' ? 'Web Mercator' : null,
  };

  let group: CoordinateScopes['group'] = null;
  const members = input.groupMembers ?? [];
  if (members.length > 0) {
    const memberCrs = members.map((m) => normalizeCrs(m.displayCrs ?? m.sourceCrs ?? null));
    const distinct = new Set(memberCrs.filter((c): c is string => c !== null));
    const representative = modeOf(memberCrs);
    const mixed = distinct.size > 1;
    group = {
      level: 'group',
      label: input.groupLabel ?? 'Group',
      crs: representative,
      note: mixed
        ? `mixed CRS across ${members.length} layers (${[...distinct].join(', ')})`
        : `${members.length} layer${members.length === 1 ? '' : 's'}`,
    };
  }

  // Mismatch: the layer's effective CRS or the group disagrees with the viewer
  // frame. A layer with no display CRS that simply renders in the viewer frame
  // is NOT a mismatch (that's expected for geographic data).
  const groupMixed =
    typeof group?.note === 'string' && group.note.startsWith('mixed');
  const layerVsViewer =
    layerCrs != null &&
    viewerFrame != null &&
    layerCrs !== viewerFrame &&
    // only when the layer explicitly targets a different display CRS
    displayCrs != null &&
    displayCrs !== viewerFrame;
  const mismatch = Boolean(groupMixed || layerVsViewer);

  return { viewer, group, layer, mismatch };
}

function bboxAssumptionFor(layer: Layer, sourceCrs: string | null): string {
  if (layer.kind === '3dtiles') {
    return 'Geographic lon/lat bbox preferred for projected/local tilesets; ECEF region/box used for globe-anchored tilesets.';
  }
  if (layer.kind === 'pointcloud') {
    return `Bounds derived from the point-cloud source; interpreted in ${sourceCrs ?? 'the source CRS'}.`;
  }
  return `[minX, minY, maxX, maxY] interpreted in ${sourceCrs ?? 'the source CRS'}.`;
}

export function buildCoordinateDiagnostics(
  layer: Layer,
  options: {
    readonly override?: LayerCoordinateOverrideInput | null;
    readonly live?: LiveRenderDiagnostics | null;
    readonly viewerFrameCrs?: string | null;
    readonly groupLabel?: string | null;
    readonly groupMembers?: ReadonlyArray<CoordinateGroupMember>;
  } = {},
): LayerCoordinateDiagnostics {
  const { override, live } = options;
  const sourceCrs = normalizeCrs(layer.sourceCrs ?? layer.crs);
  const { displayCrs, displayCrsProj4, overridden } = effectiveDisplayCrs(layer, override);
  const frameCrs = resolveFrameCrs(layer, override, live);
  const appliedScale =
    typeof live?.appliedScale === 'number' && Number.isFinite(live.appliedScale)
      ? live.appliedScale
      : null;

  const scopes = buildCoordinateScopes({
    layerId: layer.id,
    sourceCrs,
    displayCrs,
    frameCrs,
    viewerFrameCrs: options.viewerFrameCrs,
    groupLabel: options.groupLabel,
    groupMembers: options.groupMembers,
  });

  return {
    sourceCrs,
    displayCrs,
    displayCrsProj4Present: Boolean(displayCrsProj4),
    frameCrs,
    worldFrame: typeof layer.worldFrame === 'string' ? layer.worldFrame : null,
    verticalDatum: typeof layer.verticalDatum === 'string' ? layer.verticalDatum : null,
    bbox: formatBbox(layer.bbox),
    bboxCenter: bboxCenter(layer.bbox),
    bboxAssumption: bboxAssumptionFor(layer, sourceCrs),
    anchor: formatAnchor(layer),
    alignmentStatus: deriveAlignmentStatus(layer, override, live),
    appliedScale,
    renderFrame: frameCrs,
    scopes,
    override: {
      displayCrs: overridden ? displayCrs : null,
      displayCrsProj4: overridden ? displayCrsProj4 : null,
      active: overridden,
    },
  };
}
